import React, { useCallback, useState } from 'react'
import { Button, Grid, Icon, NetworkIcon, Spinner, Tooltip, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Forms/Billing/Billing-forms.module.scss'

const MIN_NUMBER_SHOWN_ACCOUNTS = 3

export const Accounts = ({
  className = '',
  t = () => {},
  accounts = null,
  currency,
  handleRemoveAccount = () => {},
  topUpAccountsCompleted = false,
}) => {
  const [numberShownAccounts, setNumberShownAccounts] = useState(MIN_NUMBER_SHOWN_ACCOUNTS)

  const breakpoint = useBreakpoint()
  const accountsLength = accounts?.length ?? 0
  const toggleNumberShownAccounts = useCallback(
    () =>
      setNumberShownAccounts((prevState) =>
        prevState === MIN_NUMBER_SHOWN_ACCOUNTS ? accountsLength : MIN_NUMBER_SHOWN_ACCOUNTS,
      ),
    [accountsLength, MIN_NUMBER_SHOWN_ACCOUNTS],
  )

  const parseError = useCallback(
    (error) =>
      typeof error === 'string'
        ? error
        : Object.values(error)
            .map((e) => (typeof e === 'string' ? e : parseError(e)))
            .join(', '),
    [],
  )

  if (!accountsLength || !currency) {
    return null
  }

  return (
    <Grid
      className={className}
      gap={15}
      direction={'column'}
      justifyContent={'start'}
      alignItems={'start'}
    >
      <table className={styles['billing__accounts-table']}>
        <thead className={styles['billing__accounts-table-head']}>
          <tr>
            <Typography
              component={'th'}
              className={styles['billing__accounts-table-cell']}
              noMargin
              variant={'Body-10'}
              color={'Label-Secondary'}
            >
              {t('billing::accounts_replenish.table.accounts')}
            </Typography>
            {!breakpoint.sm && (
              <Typography
                component={'th'}
                className={styles['billing__accounts-table-cell']}
                noMargin
                variant={'Body-10'}
                color={'Label-Secondary'}
              >
                {t('billing::accounts_replenish.table.sum')}
              </Typography>
            )}
            <Typography
              component={'th'}
              className={styles['billing__accounts-table-cell']}
              noMargin
              variant={'Body-10'}
              color={'Label-Secondary'}
            >
              {t('billing::accounts_replenish.table.status')}
            </Typography>
            <th className={styles['billing__accounts-table-cell']} />
          </tr>
        </thead>
        <tbody className={styles['billing__accounts-table-body']}>
          {accounts
            .slice(0, numberShownAccounts)
            .map(
              ({
                id,
                login,
                type,
                status = null,
                error,
                calculated: { transfer = null } = {},
              } = {}) => (
                <tr key={id} className={styles['billing__accounts-table-row']}>
                  <td className={styles['billing__accounts-table-cell']}>
                    <Grid direction={'column'} gap={8}>
                      <Grid wrap={false} gap={8} alignItems={'center'}>
                        <NetworkIcon
                          className={styles['billing__accounts-table-icon']}
                          rounded
                          width={24}
                          name={type}
                          type={'unlinked'}
                        />
                        <Typography
                          title={login}
                          component={'div'}
                          className={styles['billing__accounts-table-login']}
                          noMargin
                          variant={'Body-15'}
                          color={'Label-Fifth'}
                        >
                          {login}
                        </Typography>
                      </Grid>
                      {breakpoint.sm && (
                        <Grid wrap={false} direction={'column'} gap={4}>
                          <Typography
                            component={'div'}
                            noMargin
                            variant={'Body-10'}
                            color={'Label-Secondary'}
                          >
                            {t('billing::accounts_replenish.table.sum')}:
                          </Typography>
                          <Typography
                            component={'div'}
                            variant={'Body-10'}
                            noMargin
                            color={'Label-Fifth'}
                          >
                            <span
                              dangerouslySetInnerHTML={{
                                __html: formatMoney(
                                  transfer?.account?.value ?? 0,
                                  transfer?.account?.currency ?? currency,
                                ),
                              }}
                            />
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </td>
                  {!breakpoint.sm && (
                    <td className={styles['billing__accounts-table-cell']}>
                      <Typography variant={'Body-3'} noMargin color={'Label-Fifth'}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: formatMoney(
                              transfer?.account?.value ?? 0,
                              transfer?.account?.currency ?? currency,
                            ),
                          }}
                        />
                      </Typography>
                    </td>
                  )}
                  <td className={styles['billing__accounts-table-cell']}>
                    {status === 'loading' ? (
                      <Spinner
                        width={20}
                        color="Label-GU-Primary"
                        className={styles['billing__accounts-table-icon']}
                      />
                    ) : status === 'success' ? (
                      <Tooltip
                        className={styles['billing__accounts-table-icon']}
                        title={t('billing::accounts_replenish.table.tooltip.success')}
                      >
                        <Icon
                          className={styles['billing__accounts-table-icon']}
                          name={'status done'}
                          size={'sm'}
                          color={'Label-Confirm'}
                        />
                      </Tooltip>
                    ) : status === 'failure' ? (
                      <Tooltip
                        className={styles['billing__accounts-table-icon']}
                        title={parseError(error)}
                      >
                        <Icon
                          className={styles['billing__accounts-table-icon']}
                          name={'status warning2'}
                          size={'sm'}
                          color={'Label-Error-Primary'}
                        />
                      </Tooltip>
                    ) : (
                      <Icon
                        className={styles['billing__accounts-table-icon']}
                        name={'pause-circle'}
                        color={'Interface-Orange-Primary'}
                        size={'sm'}
                      />
                    )}
                  </td>
                  <td className={styles['billing__accounts-table-cell']}>
                    {!topUpAccountsCompleted && accountsLength > 1 && (
                      <Button
                        type={'button'}
                        variant={'ghost'}
                        size={'s'}
                        grey
                        bgColor={'none'}
                        color={'Interface-Seventh'}
                        colorHover={'Interface-Eighth'}
                        colorActive={'Interface-Eleven'}
                        startIcon={<Icon name={'close'} />}
                        onClick={() => handleRemoveAccount(id)}
                      />
                    )}
                  </td>
                </tr>
              ),
            )}
        </tbody>
      </table>
      {accountsLength > MIN_NUMBER_SHOWN_ACCOUNTS && (
        <Button
          type={'button'}
          variant={'ghost'}
          size={'l'}
          link
          onClick={toggleNumberShownAccounts}
        >
          {t(
            `billing::accounts_replenish.show_${
              numberShownAccounts === MIN_NUMBER_SHOWN_ACCOUNTS ? 'more' : 'less'
            }`,
          )}
        </Button>
      )}
    </Grid>
  )
}

Accounts.propTypes = {
  className: PropTypes.string,
  accounts: PropTypes.array,
  t: PropTypes.func,
  currency: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  handleRemoveAccount: PropTypes.func,
  topUpAccountsCompleted: PropTypes.bool,
}
